'use client';

import { AuthContextType, UserMe, UserRole } from '@/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SessionProvider, signIn, signOut, useSession } from 'next-auth/react';
import { jwtDecode } from 'jwt-decode';
import type { DecodedToken } from '@/types';
import { KEYCLOAK_CLIENT_ID } from '@/lib/next-auth/constants';
import { trackAuthAnalytics } from './trackAuthAnalytics';

const AuthContext = createContext<AuthContextType>(null!);

const ROLE_MAP: Record<string, UserRole> = {
  ROLE_MASTER: 'masterAdmin',
  ROLE_OFFICE_ADMIN: 'admin',
  ROLE_BROKER: 'broker',
};

function resolveRole(
  resource_access: DecodedToken['resource_access'],
  impersonator?: string,
): UserRole {
  const clientRoles = resource_access?.[KEYCLOAK_CLIENT_ID]?.roles ?? [];
  if (!impersonator && clientRoles.includes('ROLE_MASTER')) return 'masterAdmin';
  for (const r of clientRoles) {
    if (impersonator && r === 'ROLE_MASTER') continue;
    if (ROLE_MAP[r]) return ROLE_MAP[r];
  }
  return 'user';
}

function safeDecodeToken(accessToken: string): DecodedToken | undefined {
  try {
    return jwtDecode<DecodedToken>(accessToken);
  } catch {
    return undefined;
  }
}

function mapUser({
  name,
  email,
  given_name,
  family_name,
  phone,
  resource_access,
  impersonator,
}: DecodedToken): UserMe {
  return {
    name,
    email,
    given_name,
    family_name,
    phone,
    role: resolveRole(resource_access, impersonator),
  };
}

const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session, status } = useSession();
  const recoveryInProgress = useRef(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const accessToken = session?.accessToken;
  const hasRefreshError = session?.error === 'RefreshAccessTokenError';
  const isAuthenticated = status === 'authenticated' && !!accessToken && !hasRefreshError;

  const decodedToken = useMemo(
    () => (isAuthenticated && accessToken ? safeDecodeToken(accessToken) : undefined),
    [isAuthenticated, accessToken],
  );
  const user = decodedToken ? mapUser(decodedToken) : undefined;

  useEffect(() => {
    if (!decodedToken) return;
    trackAuthAnalytics(decodedToken);
  }, [decodedToken]);

  // When the session has a permanent refresh error, wipe the broken session cookie.
  // isRecovering is exposed via context so ProtectedLayout can wait for signOut to
  // complete before calling login() — prevents a CSRF state race where two concurrent
  // signIn() calls overwrite each other's state cookie (OAuthSignin error /api/auth/error).
  useEffect(() => {
    if (!hasRefreshError || recoveryInProgress.current) return;
    console.warn('[auth] RefreshAccessTokenError detected — signing out to clear broken session');
    recoveryInProgress.current = true;
    setIsRecovering(true);
    void signOut({ redirect: false }).finally(() => {
      console.log('[auth] signOut complete after RefreshAccessTokenError');
      recoveryInProgress.current = false;
      setIsRecovering(false);
    });
  }, [hasRefreshError]);

  const login = useCallback((callbackUrl?: string) => {
    const resolvedCallbackUrl =
      callbackUrl ??
      (() => {
        const currentUrl = window.location.pathname + window.location.search + window.location.hash;
        return currentUrl.startsWith('/callback') ? '/' : currentUrl;
      })();
    void signIn('keycloak', { callbackUrl: resolvedCallbackUrl });
  }, []);

  const logout = () => signOut({ callbackUrl: '/' });

  // Return token only when valid — interceptor skips Authorization header if undefined
  const getAccessToken = () => (isAuthenticated ? accessToken : undefined);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        getAccessToken,
        user,
        isLoggingOut: isRecovering,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Access tokens last 5 minutes. Polling every 45s lands inside the 60s refresh
// buffer in authOptions, so the token is renewed while it is still valid.
// refetchOnWindowFocus checks again when the tab becomes active.
const SESSION_REFETCH_INTERVAL = 45;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => (
  <SessionProvider refetchInterval={SESSION_REFETCH_INTERVAL} refetchOnWindowFocus>
    <AuthContextProvider>{children}</AuthContextProvider>
  </SessionProvider>
);

export const useAuth = () => useContext(AuthContext);
